/**
 * The one connection, for both pages.
 *
 * The config is read from the environment at build time rather than written
 * out here. None of it is secret (a web API key identifies a project, it does
 * not authorise anything: the Firestore rules do that) but a key pasted into a
 * repository is a key nobody updates when the project moves.
 *
 * Against the emulators the key is never checked, so anything will do. Against
 * the live project a missing key is a build that would fail on its first read
 * with a message about permissions, so it fails here instead, with a message
 * about the key.
 */

import { initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { FUNCTIONS_REGION, storageBucketFor } from "@liyu/core";

declare const __CONFIG__: {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  messagingSenderId: string;
  emulators: boolean;
  loginDomain: string;
};

export const config = __CONFIG__;

const app = initializeApp({
  apiKey: config.apiKey,
  authDomain: config.authDomain,
  projectId: config.projectId,
  appId: config.appId,
  messagingSenderId: config.messagingSenderId,
  storageBucket: storageBucketFor(config.projectId),
});

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, FUNCTIONS_REGION);
export const storage = getStorage(app);

if (config.emulators) {
  const host = window.location.hostname;
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  // The Auth emulator sends no real texts and needs no reCAPTCHA.
  auth.settings.appVerificationDisabledForTesting = true;
  connectFirestoreEmulator(db, host, 8080);
  connectFunctionsEmulator(functions, host, 5001);
  connectStorageEmulator(storage, host, 9199);
}
